import { FileRejection } from "react-dropzone";
import * as pdfjsLib from "pdfjs-dist";
import pdfWorkerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import { BaseMessage } from "@sendbird/chat/message";
import { iCity } from "../types/commonTypes";
import { fetchPdfMediaService } from "../services/mediaServices";
import { ROUTE_PATHS, ROUTE_QUERY_CONSTANTS } from "../routes";
import { DETAILS_TAB_VALUE, notficationsTabsMapping, QUOTE_TAB_VALUE } from "../constants/eventDetail";
import { iNotificationPayloadData } from "../types/notificationTypes";
import {
	ChatFilterTypeEnum,
	CorporateEventStatusEnum,
	CorporateEventTabsEnum,
	CorporateEventVendorStatusEnum,
	EventSourceEnum,
	EventTypeFilterEnum,
} from "../constants/enums";
import { ICorporateEvent } from "../interfaces/corporateEvent";
import {
	EventRequestStatusEnum,
	EventStatusEnum,
	VendorEventResponseStatusesEnum,
	VendorEventStatusEnum,
	iAddress,
	iEventType,
} from "../types/eventTypes";
import { iType } from "../types/eventTypes";
import { iEventDetailCMS } from "../types/eventTypes";
import { FILE_INVALID_TYPE_ERROR_CODE, FILE_TOO_LARGE_ERROR_CODE } from "../constants/formConstants";
import { isEventDeclined } from "../utils/B2B/corporateEventsUtils";
import { AxiosProgressEvent } from "axios";
import {
	CURRENCY,
	DEFAULT_PAGE_LIMIT_MESSAGING,
	REMOVE_SECONDS_REGEX,
	SENDBIRD_CUSTOM_MESSAGES,
} from "../constants/commonConstants";
import { ChatFilterState, IGetAllChatsParams } from "../interfaces/chats";
import { DEFAULT_DATE_FILTER_FORMAT, fDate } from "../utils/formatTime";
import { AppliedFilters } from "../components/pages-components/chat-page-components/ChatsFilter/FilterChips/FilterChips";
import { normalizeSearchQuery } from "../utils/textSearch";

// Bundled with the app, so it always matches the pdfjs-dist version (no CDN copy of each release needed).
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

export const citites: iCity[] = [
	{
		name: "Dubai",
		bussinessId: "DUBAI",
		order: 1,
	},
	{
		name: "Abu Dhabi",
		bussinessId: "ABU DHABI",
		order: 2,
	},
	{
		name: "Sharjah",
		bussinessId: "SHARJAH",
		order: 3,
	},
	{
		name: "Ajman",
		bussinessId: "AJMAN",
		order: 4,
	},
];

export const getCityMapping = (cityId: number): string | null => {
	if (cityId <= 0) return null;
	let result: iCity | undefined = citites.find((o) => o.order == cityId);
	return result != undefined ? result?.name : null;
};

export const base64StrToBlob = (baseStr: string, type: string) => {
	const binaryString = atob(baseStr);

	// Convert the binary string to a Uint8Array
	const uint8Array = new Uint8Array(binaryString.length);
	for (let i = 0; i < binaryString.length; i++) {
		uint8Array[i] = binaryString.charCodeAt(i);
	}

	return new Blob([uint8Array], {
		type: type,
	});
};

export const fetchPdfMedia = async (filePath: string) => {
	const mediaUrl = await fetchPdfMediaService(filePath);
	if (mediaUrl && typeof mediaUrl === "string") {
		// @ts-ignore
		const open = window.open(mediaUrl, "_blank");
		if (!open) {
			return false;
		}
	}
	return true;
};

export const isOverSizedFile = (acceptedFiles: File[] | File, maxSize: number) => {
	let oversizedFile: File | undefined;

	if (Array.isArray(acceptedFiles)) {
		oversizedFile = acceptedFiles.find((file) => {
			return file.size > maxSize * 1024 * 1024;
		});
	} else {
		oversizedFile = acceptedFiles.size > maxSize * 1024 * 1024 ? acceptedFiles : undefined;
	}

	return !!oversizedFile;
};

export const getDocumentPageCount = (file: File): Promise<number> => {
	return new Promise((resolve) => {
		const reader = new FileReader();

		reader.onload = async (e: ProgressEvent<FileReader>) => {
			try {
				const typedArray = new Uint8Array(e.target?.result as ArrayBuffer);
				const pdf = await pdfjsLib.getDocument({ data: typedArray }).promise;
				resolve(pdf.numPages);
			} catch (err) {
				resolve(0);
			}
		};

		reader.onerror = () => {
			resolve(0);
		};

		reader.readAsArrayBuffer(file);
	});
};

export function extractNameAndTimestamp(filename: string): { name: string; timestamp: number } {
	const lastDashIndex = filename.lastIndexOf("-");
	const lastDotIndex = filename.lastIndexOf(".");

	if (lastDashIndex === -1 || lastDotIndex === -1 || lastDashIndex > lastDotIndex) {
		return { name: filename, timestamp: Date.now() };
	}

	const timestamp = filename.substring(lastDashIndex + 1, lastDotIndex);
	const extension = filename.substring(lastDotIndex + 1);
	const name = `${filename.substring(0, lastDashIndex)}.${extension}`;

	return { name, timestamp: Number(timestamp) };
}

export const getFromCms = (value: string | number | null, cms: iEventDetailCMS): string | number => {
	if (!value) {
		return "None";
	}
	if (cms[value as keyof iEventDetailCMS]) {
		return cms[value as keyof iEventDetailCMS];
	}
	return value;
};

// Location grouping utility
export const groupLocationsByDayAndConvertToArray = (locations: any[]): Record<number, any[]> => {
	return (locations || []).reduce(
		(acc, location) => {
			if (!acc[location.dayNumber]) {
				acc[location.dayNumber] = [];
			}
			acc[location.dayNumber].push(location);
			return acc;
		},
		{} as Record<number, any[]>,
	);
};

export const getStringSortedEventTypeOfFood = (
	array: { value: string; noCms?: boolean }[],
	cms: iEventDetailCMS,
) => {
	const foodTypes = array
		.map((item) => (item.noCms ? item.value : getFromCms(item?.value, cms) || ""))
		?.filter((item) => item)
		.sort();
	return foodTypes;
};

export const convertArrayToStringForCorporateEvent = (
	arr: Array<iType> | undefined,
	cms: iEventDetailCMS,
) => {
	if (arr !== undefined && arr?.length > 0) {
		let filterArray = arr?.filter((x) => x?.value != null && x?.value !== "" && x?.value !== undefined);
		if (filterArray.length === 0) {
			return "None";
		}
		return getStringSortedEventTypeOfFood(filterArray, cms).join(", ");
	}
	return "None";
};

export const openGoogleMap = (address: iAddress | string) => {
	if (address) {
		let google_url = `https://www.google.com/maps/`;

		if (typeof address === "string") {
			google_url += `search/` + address.split(" ").join("+") + "/";
		} else {
			if (address.latitude && address.longitude) {
				google_url += `search/?api=1&query=${address.latitude},${address.longitude}&query_place_id=${address.place_id}&t=h&z=17`;
			}
			if (address.place_name) {
				google_url += `search/` + address.place_name.split(" ").join("+") + "/";
			}
		}
		window.open(google_url, "_blank");
	}
};

export const scrollToSectionHelper = (section: string) => {
	const el = document.getElementById(section);

	if (el) {
		el.scrollTop = 0;
		el.scrollIntoView({ behavior: "smooth", block: "start" });
	}
};

export const breadCrumbLinkAndTitleForCorporateEvent = (corporateEventDetail: ICorporateEvent | null) => {
	if (
		corporateEventDetail?.status === CorporateEventStatusEnum.Active &&
		corporateEventDetail?.vendor?.status === CorporateEventVendorStatusEnum.AvailableForBidding
	) {
		return {
			link: ROUTE_PATHS.B2B.ACTIVE_EVENTS,
			title: "Corporate RFPs",
		};
	} else if (isEventDeclined(corporateEventDetail as ICorporateEvent)) {
		return {
			link: ROUTE_PATHS.B2B.DECLINED_EVENTS,
			title: "Declined Events",
		};
	} else if (corporateEventDetail?.status === CorporateEventStatusEnum.Accepted) {
		return {
			link: ROUTE_PATHS.B2B.APPROVED_EVENTS,
			title: "Approved Events",
		};
	} else if (corporateEventDetail?.status === CorporateEventStatusEnum.Completed) {
		return {
			link: ROUTE_PATHS.B2B.COMPLETED_EVENTS,
			title: "Completed Events",
		};
	}
	return {
		link: ROUTE_PATHS.B2B.ACTIVE_EVENTS,
		title: "Corporate RFPs",
	};
};

export function isObjectEmpty(obj: any) {
	return obj ? Object.keys(obj).length === 0 && obj.constructor === Object : true;
}

export const checkIfFileIsTooLarge = (fileRejections: FileRejection[]) => {
	return fileRejections.some((rejection) =>
		rejection.errors.some((error) => error.code === FILE_TOO_LARGE_ERROR_CODE),
	);
};

export const checkIfFileIsInvalidType = (fileRejections: FileRejection[]) => {
	return fileRejections.some((rejection) =>
		rejection.errors.some((error) => error.code === FILE_INVALID_TYPE_ERROR_CODE),
	);
};

export const calculateUploadProgress = (progressEvent: AxiosProgressEvent) => {
	return Math.round((progressEvent.loaded * 100) / (progressEvent.total ?? 1));
};

export const getFileNameFromUrl = (url: string, index: number) => {
	if (!url) return `${Date.now()}-${index}`;

	const lastSegment = url.substring(url.lastIndexOf("/") + 1);
	const baseNameAndExtension = lastSegment?.trim();

	return baseNameAndExtension ? baseNameAndExtension : `${Date.now()}-${index}`;
};

export const isIPhone = () => {
	if (typeof navigator === "undefined") return false;

	const ua = navigator.userAgent;
	const platform = navigator.platform;

	return (
		// iPhone pretending to be Mac (desktop mode)
		(/iPhone|iPod/i.test(ua) || (platform === "MacIntel" && navigator.maxTouchPoints > 1))
	);
};

export const addTabToUrl = (url: string, tab: string) => {
	if (!url || !tab) return url;

	// Check if URL already has a tab parameter
	const hasTabParam = url.includes("?tab=") || url.includes("&tab=");

	if (hasTabParam) {
		return url; // Tab parameter already exists, return URL as is
	}

	// Add tab parameter based on whether URL already has query params
	const separator = url.includes("?") ? "&" : "?";
	return `${url}${separator}tab=${tab}`;
};

const QUOTE_NOTIFICATION_TABS = ["Quotes", CorporateEventTabsEnum.Quotes];

export const getNotificationUrl = (data: iNotificationPayloadData) => {
	const url = data.url ?? "";
	const isEventPage = /\/(event-detail|b2b\/events)\//.test(url);
	if (!isEventPage && data.selectedTab && QUOTE_NOTIFICATION_TABS.includes(data.selectedTab)) {
		const corporateEventId = data.eventType === "CORPORATE_EVENT" ? data.corporateEventId || data.eventId : undefined;
		if (corporateEventId) {
			return `${ROUTE_PATHS.B2B.EVENT_DETAIL.replace(":id", corporateEventId)}?type=${ROUTE_QUERY_CONSTANTS.CorporateActiveEvents}&tab=${CorporateEventTabsEnum.Quotes}`;
		}
		if (data.eventId) {
			return `${ROUTE_PATHS.EVENT_DETAIL.replace(":id", data.eventId)}?type=${ROUTE_QUERY_CONSTANTS.InProgress}&tab=${QUOTE_TAB_VALUE}`;
		}
	}
	return data.selectedTab
		? addTabToUrl(url, notficationsTabsMapping[data.selectedTab as keyof typeof notficationsTabsMapping] || DETAILS_TAB_VALUE)
		: url;
};

export const breadCrumbTitleAndLinkEventDetail = (eventDetail: iEventType, selectedType: string) => {
	const isRequest = !!eventDetail.request;
	let title: string | undefined;
	let link: string | undefined;

	if (
		isRequest &&
		[EventRequestStatusEnum.Active, EventRequestStatusEnum.OnHold].includes(eventDetail?.request?.status || 0)
	) {
		title =
			selectedType === ROUTE_QUERY_CONSTANTS.AiDirectRequest ? "AI Direct Requests" : "Prepaid Requests";
		link =
			selectedType === ROUTE_QUERY_CONSTANTS.AiDirectRequest
				? ROUTE_PATHS.AI_DIRECT_REQUEST
				: ROUTE_PATHS.PRE_PAID_REQUESTS;
	} else if (eventDetail?.vendorStatus === VendorEventResponseStatusesEnum.Invited) {
		title = "User Direct Request";
		link = ROUTE_PATHS.DIRECTORY_REQUEST_EVENTS;
	} else if (eventDetail?.vendorStatus === VendorEventResponseStatusesEnum.AssignedByAdmin) {
		title = "AI Direct Request";
		link = ROUTE_PATHS.AI_DIRECT_REQUEST;
	} else if (eventDetail?.vendorStatus === VendorEventResponseStatusesEnum.Bidding) {
		title = "In Progress";
		link = ROUTE_PATHS.IN_PROGRESS_EVENTS;
	} else if (eventDetail?.vendorStatus === VendorEventResponseStatusesEnum.Completed) {
		title = "Completed";
		link = ROUTE_PATHS.EVENT_HISTORY_COMPLETED;
	} else if (
		(eventDetail?.vendorStatus === VendorEventResponseStatusesEnum.Accepted && !isRequest) ||
		eventDetail?.request?.status === EventRequestStatusEnum.Accepted
	) {
		title = "Approved";
	} else if (
		eventDetail?.vendorStatus === VendorEventResponseStatusesEnum.Rejected ||
		eventDetail?.vendorStatus === VendorEventResponseStatusesEnum.RejectedByCustomer ||
		eventDetail?.vendorStatus === VendorEventResponseStatusesEnum.Cancelled ||
		[
			EventRequestStatusEnum.Cancelled,
			EventRequestStatusEnum.Reject,
			EventRequestStatusEnum.Expired,
		].includes(eventDetail?.request?.status || 0)
	) {
		title = "Declined";
		link = ROUTE_PATHS.EVENT_HISTORY_DECLINED;
	} else if (eventDetail?.vendorStatus === VendorEventResponseStatusesEnum.AvailableForBidding) {
		title = "AI Direct Requests";
		link = ROUTE_PATHS.AI_DIRECT_REQUEST;
	}

	// Handle link for vendorStatus === 4 (different condition than title)
	if (eventDetail?.vendorStatus === VendorEventResponseStatusesEnum.Accepted && !link) {
		link = ROUTE_PATHS.APPROVED_EVENTS;
	}

	// Handle link for vendorStatus === 6, 7, 8 (only when these statuses, not request status)
	if (
		(eventDetail?.vendorStatus === VendorEventResponseStatusesEnum.Rejected ||
			eventDetail?.vendorStatus === VendorEventResponseStatusesEnum.RejectedByCustomer ||
			eventDetail?.vendorStatus === VendorEventResponseStatusesEnum.Cancelled) &&
		!link
	) {
		link = ROUTE_PATHS.EVENT_HISTORY_DECLINED;
	}

	return { title, link };
};

export const isB2CEventDeclined = (eventDetail: iEventType) => {
	const eventStatus = eventDetail?.status;
	const vendorStatus = eventDetail?.vendorStatus;
	return (
		eventStatus === EventStatusEnum.Cancelled ||
		vendorStatus === VendorEventResponseStatusesEnum.RejectedByCustomer ||
		vendorStatus === VendorEventResponseStatusesEnum.Rejected ||
		vendorStatus === VendorEventResponseStatusesEnum.Cancelled
	);
};

export const isB2CEventActive = (eventDetail: iEventType) => {
	const eventStatus = eventDetail?.status;
	const vendorStatus = eventDetail?.vendorStatus;
	return (
		eventStatus === EventStatusEnum.Active &&
		(vendorStatus === VendorEventResponseStatusesEnum.AvailableForBidding ||
			vendorStatus === VendorEventResponseStatusesEnum.Bidding)
	);
};

export const getB2CEventDeclineReason = (eventDetail: iEventType) => {
	const reason =
		eventDetail?.vendorEventRecord?.cancellationReason?.value ||
		eventDetail?.vendorEventRecord?.otherDeclineReason ||
		eventDetail?.vendorEventRecord?.declineReason?.value;

	return humanizeReason(reason || "");
};

export const getAmountWithCurrency = (amount: number) =>
	amount >= 0 ? `${CURRENCY} ${amount.toFixed(2)}` : "-";

export const removingSeconds = (time: string | null | undefined) => {
	if (time) {
		const pattern = REMOVE_SECONDS_REGEX;
		time = time + " ";
		let spacePos = time.indexOf(" ");
		let firstPart = time.substring(0, spacePos - 3);
		let lastPart = time.substring(spacePos);
		let result = firstPart + lastPart;
		if (pattern.test(result.trim())) {
			return result.trim();
		}
	}

	return "None";
};

export const convertArrayToString = (arr: Array<iType> | undefined, cms: iEventDetailCMS) => {
	if (arr !== undefined && arr?.length > 0) {
		let filterArray = arr?.filter((x) => x?.value != null && x?.value !== "" && x?.value !== undefined);
		if (filterArray.length === 0) {
			return "None";
		}
		return getStringSortedEventTypeOfFood(filterArray, cms).join(", ");
	}
	return "None";
};

/**
 * Converts camelCase / PascalCase into sentence case
 * Examples:
 *  "vendorCancelled" -> "Vendor cancelled"
 *  "LateDeliveryIssue" -> "Late delivery issue"
 *  "Vendor Cancelled" -> "Vendor cancelled"
 */
export const humanizeReason = (value?: string | null): string | undefined => {
	if (!value) return undefined;

	const withSpaces = value
		// Add space before capital letters
		.replace(/([a-z])([A-Z])/g, "$1 $2")
		.trim();

	return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1).toLowerCase();
};

/**
 * @description
 * Converts chat filter state to API parameters for getAllChatsRecord
 * @param {ChatFilterState} filters - The filter state to convert
 * @returns {Partial<IGetAllChatsParams>} - The API parameters object
 */
export const buildChatsApiParamsFromFilters = (filters: ChatFilterState): Partial<IGetAllChatsParams> => {
	const params: Partial<IGetAllChatsParams> = {};

	const orderId = filters.orderId.replace(/^\s*(ord)?\s*#?\s*/i, "").trim();
	if (orderId) {
		params.orderId = orderId;
	}
	if (filters.eventDateStart) {
		params.eventDateStart = fDate(filters.eventDateStart, DEFAULT_DATE_FILTER_FORMAT);
	}
	if (filters.eventDateEnd) {
		params.eventDateEnd = fDate(filters.eventDateEnd, DEFAULT_DATE_FILTER_FORMAT);
	}
	if (filters.createdDateStart) {
		params.createdDateStart = fDate(filters.createdDateStart, DEFAULT_DATE_FILTER_FORMAT);
	}
	if (filters.createdDateEnd) {
		params.createdDateEnd = fDate(filters.createdDateEnd, DEFAULT_DATE_FILTER_FORMAT);
	}
	if (filters.vendorEventStatus.length > 0) {
		params.vendorEventStatus = filters.vendorEventStatus.map((status: string) =>
			convertVendorEventStatusToEnum(status),
		);
	}
	if (filters.eventType) {
		params.eventType = filters.eventType;
	}

	return params;
};

/**
 * @description
 * Converts event status string to enum number
 * @param {string} statusString - The status string to convert
 * @returns {number} - The corresponding enum number
 */
export const convertVendorEventStatusToEnum = (statusString: string): number => {
	switch (statusString) {
		case "Active":
			return VendorEventStatusEnum.AvailableForBiddingAiRequest; // 8
		case "Approved":
			return VendorEventStatusEnum.Accepted; // 2
		case "Completed":
			return VendorEventStatusEnum.Completed; // 6
		default:
			return 0;
	}
};

// Parse filter state from URL params
export const parseFiltersFromURL = (
	searchParams: URLSearchParams,
	allVendorIds?: string[],
): ChatFilterState => {
	const orderId = searchParams.get("orderId") || "";
	const eventDateStart = searchParams.get("eventDateStart");
	const eventDateEnd = searchParams.get("eventDateEnd");
	const createdDateStart = searchParams.get("createdDateStart");
	const createdDateEnd = searchParams.get("createdDateEnd");
	// Handle comma-separated values for eventStatus
	const vendorEventStatusParam = searchParams.get("vendorEventStatus");
	const vendorEventStatus = vendorEventStatusParam
		? vendorEventStatusParam.split(",").filter((s) => s.trim())
		: [];
	const eventType = searchParams.get("eventType") || "";

	return {
		orderId,
		eventDateStart: eventDateStart ? new Date(eventDateStart) : null,
		eventDateEnd: eventDateEnd ? new Date(eventDateEnd) : null,
		createdDateStart: createdDateStart ? new Date(createdDateStart) : null,
		createdDateEnd: createdDateEnd ? new Date(createdDateEnd) : null,
		vendorEventStatus: vendorEventStatus,
		eventType,
	};
};

// Convert filter state to applied filters for display
export const convertToAppliedFilters = (filters: ChatFilterState): AppliedFilters => {
	return {
		orderId: filters.orderId || undefined,
		eventDateStart: filters.eventDateStart
			? fDate(filters.eventDateStart, DEFAULT_DATE_FILTER_FORMAT)
			: undefined,
		eventDateEnd: filters.eventDateEnd ? fDate(filters.eventDateEnd, DEFAULT_DATE_FILTER_FORMAT) : undefined,
		createdDateStart: filters.createdDateStart
			? fDate(filters.createdDateStart, DEFAULT_DATE_FILTER_FORMAT)
			: undefined,
		createdDateEnd: filters.createdDateEnd
			? fDate(filters.createdDateEnd, DEFAULT_DATE_FILTER_FORMAT)
			: undefined,
		vendorEventStatus: filters.vendorEventStatus.length > 0 ? filters.vendorEventStatus : undefined,
		eventType: filters.eventType || undefined,
	};
};

export const getNewFilterValue = (
	filterKey: string,
	appliedFilters: ChatFilterState,
	filterId?: string,
	allVendorIds?: string[],
) => {
	const newFilters = { ...appliedFilters };
	if (filterKey === "orderId") {
		newFilters.orderId = "";
	} else if (filterKey === "eventDate") {
		newFilters.eventDateStart = null;
		newFilters.eventDateEnd = null;
	} else if (filterKey === "createdDate") {
		newFilters.createdDateStart = null;
		newFilters.createdDateEnd = null;
	} else if (filterKey === "vendorEventStatus" && filterId) {
		// Remove specific event status
		newFilters.vendorEventStatus = newFilters.vendorEventStatus.filter(
			(status: string) => status !== filterId,
		);
	} else if (filterKey === "vendorEventStatus") {
		// Remove all event statuses (for backward compatibility)
		newFilters.vendorEventStatus = [];
	} else if (filterKey === "eventType") {
		newFilters.eventType = "";
	}

	return newFilters;
};

export const getTotalFiltersCount = (appliedFilters: ChatFilterState, allVendorIds?: string[]): number => {
	const convertedFilters = convertToAppliedFilters(appliedFilters);
	let count = 0;

	count += convertedFilters.vendorEventStatus?.length || 0;
	if (convertedFilters.orderId) count += 1;
	if (convertedFilters.eventDateStart && convertedFilters.eventDateEnd) count += 1;
	if (convertedFilters.createdDateStart && convertedFilters.createdDateEnd) count += 1;
	if (convertedFilters.eventType) count += 1;
	return count;
};

/**
 * @description
 * Builds URLSearchParams from chat filters, search term, and filter type
 * @param {ChatFilterState} filters - The filter state
 * @param {string} searchTerm - The search term
 * @param {ChatFilterTypeEnum} filterType - The filter type (All or Unread)
 * @param {string[]} allVendorIds - Optional array of all vendor IDs to check if all are selected
 * @returns {URLSearchParams} - The URL search params object
 */
export const buildChatsURLSearchParams = (
	filters: ChatFilterState,
	searchTerm: string = "",
	filterType: ChatFilterTypeEnum = ChatFilterTypeEnum.All,
	allVendorIds?: string[],
): URLSearchParams => {
	const searchParams = new URLSearchParams();

	// Add search term
	if (searchTerm.trim()) {
		searchParams.set("search", searchTerm.trim());
	}

	// Add unreadOnly filter
	if (filterType === ChatFilterTypeEnum.Unread) {
		searchParams.set("isUnreadOnly", "true");
	}

	if (filters.orderId) {
		searchParams.set("orderId", filters.orderId);
	}
	if (filters.eventDateStart) {
		searchParams.set("eventDateStart", fDate(filters.eventDateStart, DEFAULT_DATE_FILTER_FORMAT));
	}
	if (filters.eventDateEnd) {
		searchParams.set("eventDateEnd", fDate(filters.eventDateEnd, DEFAULT_DATE_FILTER_FORMAT));
	}
	if (filters.createdDateStart) {
		searchParams.set("createdDateStart", fDate(filters.createdDateStart, DEFAULT_DATE_FILTER_FORMAT));
	}
	if (filters.createdDateEnd) {
		searchParams.set("createdDateEnd", fDate(filters.createdDateEnd, DEFAULT_DATE_FILTER_FORMAT));
	}
	if (filters.vendorEventStatus.length > 0) {
		searchParams.set("vendorEventStatus", filters.vendorEventStatus.join(","));
	}
	if (filters.eventType) {
		searchParams.set("eventType", filters.eventType);
	}

	return searchParams;
};

export const buildApiParamsHelper = (
	debouncedSearchTerm: string,
	filterType: ChatFilterTypeEnum,
	appliedFilters: ChatFilterState,
	currentPage: number = 1,
	limit: number = DEFAULT_PAGE_LIMIT_MESSAGING,
) => {
	const params: any = {
		page: currentPage,
		limit: limit,
		search: normalizeSearchQuery(debouncedSearchTerm) || undefined,
		unreadOnly: filterType === ChatFilterTypeEnum.Unread ? true : undefined,
		...buildChatsApiParamsFromFilters(appliedFilters),
	};
	return params;
};

export const getEventTypeFromEventObject = (event: iEventType | ICorporateEvent) => {
	if ("locations" in event) {
		return EventTypeFilterEnum.B2B;
	}

	let b2cEvent = event as iEventType;
	if (b2cEvent.source === EventSourceEnum.shopify) {
		return EventTypeFilterEnum.Prepaid;
	}
	return EventTypeFilterEnum.B2C;
};

/**
 * @description
 * Used in ROUTE_PATHS.CHATS.CHATS_DETAIL to show the event status dropdown
 * Checks if the event status dropdown should be shown
 * @param {iEventType | ICorporateEvent} eventDetail - The event detail
 * @param {EventTypeFilterEnum} eventType - The event type
 * @returns {boolean} - True if the event status dropdown should be shown, false otherwise
 */
export const showDeclineEventButtonHelper = (
	eventDetail: iEventType | ICorporateEvent,
	eventType: EventTypeFilterEnum,
) => {
	if (!eventDetail || !eventType) return false;
	if (eventType === EventTypeFilterEnum.Prepaid) {
		if ((eventDetail as iEventType).request?.status === EventRequestStatusEnum.Active) return true;
		return false;
	}
	if (eventType === EventTypeFilterEnum.B2B && eventDetail.status === CorporateEventStatusEnum.Active) {
		const b2bActiveStatuses = [
			CorporateEventVendorStatusEnum.AvailableForBidding,
			CorporateEventVendorStatusEnum.Bidding,
		];
		if (
			b2bActiveStatuses.includes(
				(eventDetail as ICorporateEvent).vendor?.status as CorporateEventVendorStatusEnum,
			)
		) {
			return true;
		} else {
			return false;
		}
	}

	if (eventType === EventTypeFilterEnum.B2C && eventDetail.status === EventStatusEnum.Active) {
		// eventDetail?.vendorStatus === VendorEventResponseStatusesEnum
		const b2cActiveStatuses = [
			VendorEventResponseStatusesEnum.AvailableForBidding,
			VendorEventResponseStatusesEnum.Bidding,
			VendorEventResponseStatusesEnum.AssignedByAdmin,
		];
		if (b2cActiveStatuses.includes(eventDetail?.vendorStatus as VendorEventResponseStatusesEnum)) {
			return true;
		} else {
			return false;
		}
	} else return false;
};

export const isMobileDevice = () => {
	const userAgent = navigator?.userAgent;

	if (!userAgent) {
		return {
			isIOS: false,
			isAndroid: false,
		};
	}
	const isIOS = /iPhone|iPad|iPod/i.test(userAgent);
	const isAndroid = /Android/i.test(userAgent);

	return {
		isIOS: isIOS,
		isAndroid: isAndroid,
	};
};

export const getEventStatusText = (
	status: EventStatusEnum | CorporateEventStatusEnum,
	eventType: EventTypeFilterEnum,
	removeEventPrefix: boolean = false,
): { status: EventStatusEnum | CorporateEventStatusEnum; text: string } => {
	let result = {
		status: status,
		text: "",
	};
	if (eventType === EventTypeFilterEnum.B2B) {
		switch (status) {
			case CorporateEventStatusEnum.Active:
				result = {
					status: CorporateEventStatusEnum.Active,
					text: "Event Active",
				};
				break;
			case CorporateEventStatusEnum.Accepted:
				result = {
					status: CorporateEventStatusEnum.Accepted,
					text: "Event Approved",
				};
				break;
			case CorporateEventStatusEnum.Completed:
				result = {
					status: CorporateEventStatusEnum.Completed,
					text: "Event Completed",
				};
				break;
			case CorporateEventStatusEnum.Cancelled:
				result = {
					status: CorporateEventStatusEnum.Cancelled,
					text: "Event Cancelled",
				};
				break;
			default:
				result = {
					status: status,
					text: "",
				};
				break;
		}
		if (removeEventPrefix) {
			result.text = result.text.replace("Event ", "");
		}
		return result;
	} else {
		switch (status) {
			case EventStatusEnum.Active:
				result = {
					status: EventStatusEnum.Active,
					text: "Event Active",
				};
				break;
			case EventStatusEnum.Accepted:
				result = {
					status: EventStatusEnum.Accepted,
					text: "Event Approved",
				};
				break;
			case EventStatusEnum.Completed:
				result = {
					status: EventStatusEnum.Completed,
					text: "Event Completed",
				};
				break;
			case EventStatusEnum.Cancelled:
				result = {
					status: EventStatusEnum.Cancelled,
					text: "Event Cancelled",
				};
				break;
			default:
				result = {
					status: status,
					text: "",
				};
				break;
		}
		if (removeEventPrefix) {
			result.text = result.text.replace("Event ", "");
		}
		return result;
	}
};

export const getVendorEventStatusText = (
	status: VendorEventStatusEnum | CorporateEventVendorStatusEnum,
	eventType: EventTypeFilterEnum,
	removeEventPrefix: boolean = false,
): { status: VendorEventStatusEnum | CorporateEventVendorStatusEnum; text: string } => {
	let result = {
		status: status,
		text: "",
	};
	if (eventType === EventTypeFilterEnum.B2B) {
		switch (status) {
			case CorporateEventVendorStatusEnum.AvailableForBidding:
				result = {
					status: CorporateEventVendorStatusEnum.AvailableForBidding,
					text: "Event Active",
				};
				break;
			case CorporateEventVendorStatusEnum.Accepted:
				result = {
					status: CorporateEventVendorStatusEnum.Accepted,
					text: "Event Approved",
				};
				break;
			case CorporateEventVendorStatusEnum.Completed:
				result = {
					status: CorporateEventVendorStatusEnum.Completed,
					text: "Event Completed",
				};
				break;
			default:
				result = {
					status: status,
					text: "",
				};
				break;
		}
		if (removeEventPrefix) {
			result.text = result.text.replace("Event ", "");
		}
		return result;
	} else {
		switch (status) {
			case VendorEventStatusEnum.Bidding:
				result = {
					status: VendorEventStatusEnum.Bidding,
					text: "Event Active",
				};
				break;
			case VendorEventStatusEnum.AvailableForBiddingAiRequest:
				result = {
					status: VendorEventStatusEnum.AvailableForBiddingAiRequest,
					text: "Event Active",
				};
				break;
			case VendorEventStatusEnum.Accepted:
				result = {
					status: VendorEventStatusEnum.Accepted,
					text: "Event Approved",
				};
				break;
			case VendorEventStatusEnum.Completed:
				result = {
					status: VendorEventStatusEnum.Completed,
					text: "Event Completed",
				};
				break;
			default:
				result = {
					status: status,
					text: "",
				};
				break;
		}
		if (removeEventPrefix) {
			result.text = result.text.replace("Event ", "");
		}
		return result;
	}
};

export const removingSecondsFromTime = (time: string | null | undefined) => {
	if (time) {
		const pattern = /^(0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/;
		time = time + " ";
		let spacePos = time.indexOf(" ");
		let firstPart = time.substring(0, spacePos - 3);
		let lastPart = time.substring(spacePos);
		let result = firstPart + lastPart;
		if (pattern.test(result.trim())) {
			return result.trim();
		}
	}

	return "None";
};

export const getEventStatusType = (
	status: EventStatusEnum | CorporateEventStatusEnum,
	eventType: EventTypeFilterEnum,
) => {
	if (eventType === EventTypeFilterEnum.B2B) {
		if (status === CorporateEventStatusEnum.Active) return "active";
		if (status === CorporateEventStatusEnum.Completed) return "completed";
		if (status === CorporateEventStatusEnum.Cancelled) return "cancelled";
		if (status === CorporateEventStatusEnum.Accepted) return "approved";
		return "active";
	}
	if (status === EventStatusEnum.Active) return "in_progress";
	if (status === EventStatusEnum.Accepted) return "approved";
	if (status === EventStatusEnum.Completed) return "completed";
	if (status === EventStatusEnum.Cancelled) return "cancelled";
	return "active";
};

export const getLastMessageText = (lastMessage: BaseMessage | null) => {
	if (!lastMessage) return "";

	const mimeType = (lastMessage as any)?.type as string | undefined;
	if (!mimeType) return lastMessage?.message;

	// Extract top-level MIME type (audio / image / application / video / etc.)
	const mainType = mimeType.split("/")[0];

	if (mainType === "audio") {
		return SENDBIRD_CUSTOM_MESSAGES.AUDIO;
	}

	if (mainType === "image") {
		return SENDBIRD_CUSTOM_MESSAGES.IMAGE;
	}

	// All documents, archives, videos, etc. fall under application/video
	if (mainType === "application" || mainType === "video") {
		return SENDBIRD_CUSTOM_MESSAGES.FILE;
	}

	return lastMessage?.message;
};

export const createRoutePathForChatDetail = (eventId: string, chatId: string) => {
	return ROUTE_PATHS.CHAT_DETAIL.replace(":eventId", eventId).replace(":chatId", chatId);
};
